import { useState, useCallback, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const ENROLL_DEFAULTS = {
  batch_id: "",
  tenure_id: "",
  start_date: "",
  cohort_id: "",
  charge_type: "full",
  discount_type: "",
  discount_value: "",
  // Feb 2026 milestone-renewal fields (only sent when milestone_mode = true)
  renewal_mode: "tenure",   // "tenure" | "session"
  session_count: "",
  is_prorated: false,
};

const RENEW_DEFAULTS = {
  batch_id: "",
  tenure_id: "",
  start_date: "",
  charge_type: "full",
  discount_type: "",
  discount_value: "",
  renewal_mode: "tenure",
  session_count: "",
  is_prorated: false,
};

/**
 * Hook to centralise enrollment + renewal form state and proration preview fetching.
 *
 * @param {Object} opts
 * @param {Array} opts.batches      — list of batch objects (must include style_id)
 * @param {Array} opts.pricing      — list of pricing objects (style_id, tenure_id, pricing_id)
 * @param {"enroll"|"renew"} opts.mode  — flow type
 * @param {string} [opts.fixedBatchId] — for renew mode, the locked batch
 * @param {string} [opts.previousEndDate] — for renew mode, ISO date of the
 *     previous enrollment's end_date; used to auto-default start_date to
 *     `previousEndDate + 1 day` (Feb 2026 spec Q3 addendum). Ignored when
 *     `useMilestoneMode` is false.
 * @param {boolean} [opts.useMilestoneMode] — Feb 2026: when true, the hook
 *     talks to `/enrollments/preview-renewal` and the resolver-based
 *     write endpoints; when false, legacy calendar-month path is used.
 *
 * @returns {{ form, setForm, preview, previewLoading, activeCohort, handlers, reset, isMilestoneMode }}
 */
export const useEnrollmentForm = ({
  batches,
  pricing,
  mode,
  fixedBatchId,
  previousEndDate,
  useMilestoneMode = false,
}) => {
  const isRenew = mode === "renew";
  const initial = isRenew
    ? { ...RENEW_DEFAULTS, batch_id: fixedBatchId || "" }
    : ENROLL_DEFAULTS;

  const [form, setForm] = useState(initial);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [activeCohort, setActiveCohort] = useState(null);

  // Renew mode: when the parent's selectedEnrollment changes after the hook
  // was first created, sync the form's batch_id AND seed the start_date to
  // previousEndDate + 1 day (Feb 2026 Q3 addendum — "fair by default").
  useEffect(() => {
    if (isRenew && fixedBatchId) {
      setForm((prev) => {
        if (prev.batch_id === fixedBatchId && prev.start_date) return prev;
        // Seed start_date = previousEndDate + 1 day when available
        let seededStart = prev.start_date;
        if (useMilestoneMode && previousEndDate && !seededStart) {
          const d = new Date(previousEndDate);
          if (!isNaN(d.getTime())) {
            d.setDate(d.getDate() + 1);
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, "0");
            const dd = String(d.getDate()).padStart(2, "0");
            seededStart = `${y}-${m}-${dd}`;
          }
        }
        return { ...prev, batch_id: fixedBatchId, start_date: seededStart };
      });
    }
  }, [isRenew, fixedBatchId, previousEndDate, useMilestoneMode]);

  const getPrice = useCallback(
    (styleId, tenureId) => pricing.find((p) => p.style_id === styleId && p.tenure_id === tenureId),
    [pricing]
  );

  const fetchPreview = useCallback(
    async (next) => {
      const batchId = next.batch_id;
      const startDate = next.start_date;
      if (!batchId || !startDate) {
        setPreview(null);
        return;
      }
      // ---- Milestone-mode branch (Feb 2026) ----
      if (useMilestoneMode) {
        if (next.renewal_mode === "tenure" && !next.tenure_id) {
          setPreview(null);
          return;
        }
        if (next.renewal_mode === "session" && !(next.session_count && parseInt(next.session_count) > 0)) {
          setPreview(null);
          return;
        }
        setPreviewLoading(true);
        try {
          const payload = {
            batch_id: batchId,
            effective_date: startDate,
            mode: next.renewal_mode,
            tenure_id: next.renewal_mode === "tenure" ? next.tenure_id : undefined,
            session_count: next.renewal_mode === "session" ? parseInt(next.session_count) : undefined,
            discount_type: next.discount_type || undefined,
            discount_value: next.discount_value !== "" && next.discount_value !== undefined ? parseFloat(next.discount_value) || 0 : undefined,
          };
          const res = await api.post("/enrollments/preview-renewal", payload);
          setPreview(res.data);
        } catch (error) {
          toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || "Failed to calculate preview");
          setPreview(null);
        } finally {
          setPreviewLoading(false);
        }
        return;
      }

      // ---- Legacy calendar-month branch ----
      const tenureId = next.tenure_id;
      if (!tenureId) {
        setPreview(null);
        return;
      }
      const batch = batches.find((b) => b.batch_id === batchId);
      const price = getPrice(batch?.style_id, tenureId);
      if (!price) {
        setPreview(null);
        return;
      }
      setPreviewLoading(true);
      try {
        const payload = {
          batch_id: batchId,
          pricing_id: price.pricing_id,
          join_date: new Date(startDate).toISOString(),
        };
        payload.charge_type = next.charge_type || "full";
        if (next.discount_type) payload.discount_type = next.discount_type;
        if (next.discount_value !== "" && next.discount_value !== undefined) {
          payload.discount_value = parseFloat(next.discount_value) || 0;
        }
        if (!isRenew && next.cohort_id) {
          payload.cohort_id = next.cohort_id;
        }
        const res = await api.post("/enrollments/preview", payload);
        setPreview(res.data.preview);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to calculate proration");
        setPreview(null);
      } finally {
        setPreviewLoading(false);
      }
    },
    [batches, getPrice, isRenew, useMilestoneMode]
  );

  const fetchActiveCohort = useCallback(async (batchId) => {
    try {
      const res = await api.get(`/cohorts?batch_id=${batchId}`);
      const active = (res.data.cohorts || []).find(
        (c) => c.status === "active" || c.status === "interest_capture"
      );
      setActiveCohort(active || null);
      return active;
    } catch {
      setActiveCohort(null);
      return null;
    }
  }, []);

  // ---- Handlers ----

  const setBatch = useCallback(
    async (batch_id) => {
      let defaultStart = "";
      if (!isRenew && batch_id) {
        const b = batches.find((x) => x.batch_id === batch_id);
        const bStart = b?.start_date ? new Date(b.start_date) : null;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const pick = bStart && bStart > today ? bStart : today;
        const y = pick.getFullYear();
        const m = String(pick.getMonth() + 1).padStart(2, "0");
        const d = String(pick.getDate()).padStart(2, "0");
        defaultStart = `${y}-${m}-${d}`;
      }

      const next = isRenew
        ? { ...form, batch_id }
        : {
            ...form,
            batch_id,
            tenure_id: "",
            start_date: defaultStart,
            cohort_id: "",
            charge_type: "full",
            discount_type: "",
            discount_value: "",
            session_count: "",
            is_prorated: false,
          };
      setForm(next);
      setPreview(null);
      if (!isRenew) {
        setActiveCohort(null);
        if (batch_id) {
          const cohort = await fetchActiveCohort(batch_id);
          if (cohort) {
            setForm((prev) => ({
              ...prev,
              cohort_id: cohort.cohort_id,
              tenure_id: cohort.tenure_id || "",
            }));
          }
        }
      }
    },
    [form, isRenew, fetchActiveCohort, batches]
  );

  const setTenure = useCallback(
    (tenure_id) => {
      const next = { ...form, tenure_id };
      setForm(next);
      fetchPreview(next);
    },
    [form, fetchPreview]
  );

  const setStartDate = useCallback(
    (start_date) => {
      const next = { ...form, start_date };
      setForm(next);
      fetchPreview(next);
    },
    [form, fetchPreview]
  );

  const setChargeType = useCallback(
    (charge_type) => {
      // Milestone mode: charge_type toggle maps to is_prorated bool.
      const next = useMilestoneMode
        ? { ...form, charge_type, is_prorated: charge_type === "prorated" }
        : { ...form, charge_type };
      setForm(next);
      fetchPreview(next);
    },
    [form, fetchPreview, useMilestoneMode]
  );

  const setDiscountType = useCallback(
    (discount_type) => {
      const next = { ...form, discount_type, discount_value: "" };
      setForm(next);
      fetchPreview(next);
    },
    [form, fetchPreview]
  );

  const setDiscountValue = useCallback(
    (discount_value) => {
      const next = { ...form, discount_value };
      setForm(next);
      fetchPreview(next);
    },
    [form, fetchPreview]
  );

  // Feb 2026 — milestone-mode handlers
  const setRenewalMode = useCallback(
    (renewal_mode) => {
      const next = {
        ...form,
        renewal_mode,
        // Reset the mode-specific field when switching
        tenure_id: renewal_mode === "tenure" ? form.tenure_id : "",
        session_count: renewal_mode === "session" ? form.session_count : "",
        is_prorated: renewal_mode === "tenure" ? form.is_prorated : false,
      };
      setForm(next);
      fetchPreview(next);
    },
    [form, fetchPreview]
  );

  const setSessionCount = useCallback(
    (session_count) => {
      const next = { ...form, session_count };
      setForm(next);
      fetchPreview(next);
    },
    [form, fetchPreview]
  );

  const reset = useCallback(() => {
    setForm(isRenew ? { ...RENEW_DEFAULTS, batch_id: fixedBatchId || "" } : ENROLL_DEFAULTS);
    setPreview(null);
    setPreviewLoading(false);
    setActiveCohort(null);
  }, [isRenew, fixedBatchId]);

  return {
    form,
    setForm,
    preview,
    previewLoading,
    activeCohort,
    getPrice,
    isMilestoneMode: !!useMilestoneMode,
    handlers: {
      setBatch,
      setTenure,
      setStartDate,
      setChargeType,
      setDiscountType,
      setDiscountValue,
      setRenewalMode,
      setSessionCount,
    },
    reset,
  };
};

export default useEnrollmentForm;
